
import type { UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useMemo } from "react";

import type { WerkzoekenCampaignIntegrationDto } from "./types";
import { useWerkzoekenCampaignIntegrations } from "./useWerkzoekenCampaignIntegrations";
import { registerCampaignsIntegrationDataHook } from "../../hooks/integrationDataHooks";
import { registerCampaignsItemsDataHook } from "../../hooks/itemsDataHooks";
import { registerCampaignEnrichment } from "../../hooks/useCampaignIntegrationsInfinite";

import type { ModuleDataValue } from "@/features/integrations/shared/renderer/hooks/types";
import { formatWhole, formatCurrency } from "@/utils/valueFormat";

registerCampaignsIntegrationDataHook("werkzoeken", function useWerkzoekenCampaignsIntegrationData(params) {
  const { clientId } = params;
  const queryResult = useWerkzoekenCampaignIntegrations({ clientId });

  const transformedData = useMemo(() => {
    const summary = queryResult.data?.summary;
    if (!summary) return undefined;

    return {
      totalCampaigns: summary.totalCampaigns,
      activeOnWerkzoeken: summary.activeOnWerkzoeken,
      syncedCount: summary.syncedCount,
      syncedLabel: summary.syncedCount,
      syncedSubtitle: `Of ${formatWhole(summary.totalCampaigns)} Werkzoeken campaigns`,
    };
  }, [queryResult.data?.summary]);

  return {
    ...queryResult,
    data: transformedData,
  } as unknown as UseQueryResult<Record<string, ModuleDataValue>, AxiosError>;
});

registerCampaignsItemsDataHook("werkzoeken", (clientId) =>
  clientId ? useWerkzoekenCampaignsItemsDataImpl : undefined
);

function useWerkzoekenCampaignsItemsDataImpl(params: {
  clientId: string | undefined;
  tenantId: string | undefined;
  page: number;
  pageSize: number;
  search?: string;
  filterValues?: Record<string, string>;
}) {
  const { clientId, page, pageSize, search, filterValues } = params;
  const statusFilter = filterValues?.statusFilter;
  const queryResult = useWerkzoekenCampaignIntegrations({ clientId, page, pageSize, search, statusFilter });

  const transformedData = useMemo(() => {
    if (!queryResult.data) return undefined;

    const enrichedItems = (queryResult.data.items ?? []).map((item: WerkzoekenCampaignIntegrationDto) => {
      const falconStatusBadge = item.falconStatus === "Active"
        ? { label: "common.active", variant: "Success" }
        : item.falconStatus === "Paused"
          ? { label: "common.paused", variant: "Warning" }
          : { label: "common.inactive", variant: "Default" };

      const syncStatusBadge = item.syncStatus === "Synced"
        ? { label: "integrations.werkzoeken.campaigns.synced", variant: "Success" }
        : item.syncStatus === "NotSynced"
          ? { label: "integrations.werkzoeken.campaigns.notSynced", variant: "Warning" }
          : { label: "integrations.werkzoeken.campaigns.failed", variant: "Error" };

      const startDateFormatted = item.startDate ? new Date(item.startDate).toLocaleDateString() : "";
      const endDateFormatted = item.endDate ? new Date(item.endDate).toLocaleDateString() : "—";

      const budgetFormatted = item.monthlyBudget != null
        ? formatCurrency(item.monthlyBudget)
        : "";

      const currentMonthBudgetFormatted = item.currentMonthBudget != null
        ? formatCurrency(item.currentMonthBudget)
        : null;
      const remoteBudgetFormatted = item.remoteBudget != null
        ? formatCurrency(item.remoteBudget)
        : null;

      const remoteStatusLower = item.werkzoekenStatus?.toLowerCase();
      const werkzoekenStatusInline = item.syncStatus === "Failed"
        ? { label: "integrations.werkzoeken.campaigns.issuesFound", variant: "warning" }
        : remoteStatusLower === "active"
          ? { label: "integrations.werkzoeken.campaigns.werkzoekenActive", variant: "success" }
          : remoteStatusLower === "paused"
            ? { label: "common.paused", variant: "warning" }
            : { label: "integrations.werkzoeken.campaigns.werkzoekenInactive", variant: "neutral" };

      const remoteStatusValue = remoteStatusLower === "active" ? "Active"
        : remoteStatusLower === "paused" ? "Paused"
          : "Inactive";

      return {
        ...item,
        falconStatusBadge,
        syncStatusBadge,
        startDateFormatted,
        endDateFormatted,
        budgetFormatted,
        currentMonthBudgetFormatted,
        remoteBudgetFormatted,
        werkzoekenStatusInline,
        remoteStatusValue,
      };
    });

    return {
      data: enrichedItems,
      totalRecords: queryResult.data.totalRecords,
    };
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: transformedData,
  } as unknown as UseQueryResult<{ data: unknown[]; totalRecords: number }, AxiosError>;
}

registerCampaignEnrichment("werkzoeken", (items) =>
  (items as WerkzoekenCampaignIntegrationDto[]).map((item) => {
    const falconStatusBadge = item.falconStatus === "Active"
      ? { label: "common.active", variant: "Success" }
      : item.falconStatus === "Paused"
        ? { label: "common.paused", variant: "Warning" }
        : { label: "common.inactive", variant: "Default" };

    const syncStatusBadge = item.syncStatus === "Synced"
      ? { label: "integrations.werkzoeken.campaigns.synced", variant: "Success" }
      : item.syncStatus === "NotSynced"
        ? { label: "integrations.werkzoeken.campaigns.notSynced", variant: "Warning" }
        : { label: "integrations.werkzoeken.campaigns.failed", variant: "Error" };

    const startDateFormatted = item.startDate ? new Date(item.startDate).toLocaleDateString() : "";
    const endDateFormatted = item.endDate ? new Date(item.endDate).toLocaleDateString() : "—";

    const budgetFormatted = item.monthlyBudget != null
      ? formatCurrency(item.monthlyBudget)
      : "";

    const currentMonthBudgetFormatted = item.currentMonthBudget != null
      ? formatCurrency(item.currentMonthBudget)
      : null;
    const remoteBudgetFormatted = item.remoteBudget != null
      ? formatCurrency(item.remoteBudget)
      : null;

    const remoteStatusLower = item.werkzoekenStatus?.toLowerCase();
    const werkzoekenStatusInline = item.syncStatus === "Failed"
      ? { label: "integrations.werkzoeken.campaigns.issuesFound", variant: "warning" }
      : remoteStatusLower === "active"
        ? { label: "integrations.werkzoeken.campaigns.werkzoekenActive", variant: "success" }
        : remoteStatusLower === "paused"
          ? { label: "common.paused", variant: "warning" }
          : { label: "integrations.werkzoeken.campaigns.werkzoekenInactive", variant: "neutral" };

    const remoteStatusValue = remoteStatusLower === "active" ? "Active"
      : remoteStatusLower === "paused" ? "Paused"
        : "Inactive";

    return {
      ...item,
      falconStatusBadge,
      syncStatusBadge,
      startDateFormatted,
      endDateFormatted,
      budgetFormatted,
      currentMonthBudgetFormatted,
      remoteBudgetFormatted,
      werkzoekenStatusInline,
      remoteStatusValue,
    };
  }),
);

export { useWerkzoekenCampaignIntegrations } from "./useWerkzoekenCampaignIntegrations";
export type {
  WerkzoekenCampaignIntegrationDto,
  WerkzoekenCampaignIntegrationsResponse,
} from "./types";
